"use client";

import { useAtomValue } from "@effect/atom-react";
import { isAtomCommandInterrupted } from "@t3tools/client-runtime/state/runtime";
import type { EnvironmentId, ThreadId, VoiceConversationEvent } from "@t3tools/contracts";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";

import { toastManager } from "~/components/ui/toast";
import { VoiceAudioPlayer, VoiceTurnRecorder } from "~/lib/voiceAudio";
import { useAtomCommand } from "~/state/use-atom-command";
import { voiceEnvironment } from "~/state/voice";

// --------------------------------------------------------------------------
// Event-derived state
// --------------------------------------------------------------------------

/** Conversation phase driven purely by server events. */
export type VoiceConversationPhase =
  | "connecting"
  | "listening"
  | "thinking"
  | "speaking"
  | "error"
  | "closed";

interface VoiceConversationSnapshot {
  readonly phase: VoiceConversationPhase;
  readonly userTranscript: string;
  readonly assistantText: string;
  readonly activeToolLabel: string | null;
  readonly errorMessage: string | null;
}

const INITIAL_SNAPSHOT: VoiceConversationSnapshot = {
  phase: "connecting",
  userTranscript: "",
  assistantText: "",
  activeToolLabel: null,
  errorMessage: null,
};

function reduceVoiceEvent(
  state: VoiceConversationSnapshot,
  event: VoiceConversationEvent,
): VoiceConversationSnapshot {
  switch (event.type) {
    case "listening":
      return { ...state, phase: "listening" };
    case "user_transcript_delta":
      return { ...state, userTranscript: state.userTranscript + event.delta };
    case "user_transcript_final":
      return { ...state, userTranscript: event.transcript.text };
    case "turn_started":
      return { ...state, phase: "thinking", assistantText: "", activeToolLabel: null };
    case "tool_started":
      return { ...state, phase: "thinking", activeToolLabel: event.label };
    case "tool_completed":
      return { ...state, phase: "thinking", activeToolLabel: null };
    case "assistant_text_delta":
      return { ...state, phase: "speaking", assistantText: state.assistantText + event.delta };
    case "assistant_audio_delta":
      return state.phase === "thinking" ? { ...state, phase: "speaking" } : state;
    case "assistant_audio_done":
      return state;
    case "turn_completed":
      return { ...state, phase: "listening", activeToolLabel: null };
    case "interrupted":
      return { ...state, phase: "listening" };
    case "error":
      return { ...state, phase: "error", errorMessage: event.message };
    case "closed":
      return { ...state, phase: "closed" };
    default:
      return state;
  }
}

/**
 * Holds the reduced conversation state plus the audio player for a single live
 * session. Exposed to React via {@link useSyncExternalStore} so that every
 * server event (including bursts of deltas) is folded in without being coalesced
 * away by React batching.
 */
class VoiceConversationStore {
  private state: VoiceConversationSnapshot = INITIAL_SNAPSHOT;
  private readonly listeners = new Set<() => void>();
  private readonly player = new VoiceAudioPlayer();

  getSnapshot = (): VoiceConversationSnapshot => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  applyEvent(event: VoiceConversationEvent): void {
    if (event.type === "assistant_audio_delta") {
      void this.player.enqueue(event.audio);
    } else if (event.type === "interrupted" || event.type === "closed") {
      this.player.reset();
    }
    this.set(reduceVoiceEvent(this.state, event));
  }

  /** Reset transcripts at the start of a new user turn. */
  beginUserTurn(): void {
    this.set({ ...this.state, userTranscript: "", assistantText: "", errorMessage: null });
  }

  async dispose(): Promise<void> {
    this.listeners.clear();
    await this.player.dispose();
  }

  private set(next: VoiceConversationSnapshot): void {
    if (next === this.state) return;
    this.state = next;
    for (const listener of this.listeners) listener();
  }
}

const inactiveStore = new VoiceConversationStore();
const inactiveSyncAtom = Atom.make<VoiceConversationStore>(() => inactiveStore).pipe(
  Atom.withLabel("voice:conversation-sync:inactive"),
);

// --------------------------------------------------------------------------
// Public hook
// --------------------------------------------------------------------------

/** UI-facing status: event phase plus local push-to-talk states. */
export type VoiceConversationStatus = VoiceConversationPhase | "recording" | "sending";

export interface VoiceConversationController {
  readonly status: VoiceConversationStatus;
  readonly userTranscript: string;
  readonly assistantText: string;
  readonly activeToolLabel: string | null;
  readonly errorMessage: string | null;
  readonly isRecording: boolean;
  readonly isMuted: boolean;
  readonly isBusy: boolean;
  readonly canToggleTurn: boolean;
  /** Push-to-talk: tap to start recording, tap again to send the turn. */
  toggleTurn: () => void;
  toggleMute: () => void;
  end: () => void;
}

export interface UseVoiceConversationParams {
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId | null;
  readonly active: boolean;
  readonly onRequestClose: () => void;
}

export function useVoiceConversation(
  params: UseVoiceConversationParams,
): VoiceConversationController {
  const { environmentId, threadId, active, onRequestClose } = params;

  const startConversation = useAtomCommand(voiceEnvironment.startConversation, {
    label: "voice conversation start",
  });
  const appendAudio = useAtomCommand(voiceEnvironment.appendConversationAudio, {
    label: "voice conversation append audio",
  });
  const endInput = useAtomCommand(voiceEnvironment.endConversationInput, {
    label: "voice conversation end input",
  });
  const stopConversation = useAtomCommand(voiceEnvironment.stopConversation, {
    label: "voice conversation stop",
  });

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [turnState, setTurnState] = useState<"idle" | "recording" | "sending">("idle");
  const [isMuted, setIsMuted] = useState(false);
  const recorderRef = useRef<VoiceTurnRecorder | null>(null);
  const sequenceRef = useRef(0);

  // Build a live-events subscription atom + store for the current session.
  const session = useMemo(() => {
    if (!sessionId) return null;
    const eventsAtom = voiceEnvironment.conversationEvents({
      environmentId,
      input: { sessionId },
    });
    const store = new VoiceConversationStore();
    const atom = Atom.make<VoiceConversationStore>((get) => {
      let version = 0;
      get.addFinalizer(() => {
        void store.dispose();
      });
      const initial = get.once(eventsAtom);
      get.subscribe(eventsAtom, (result) => {
        version += 1;
        if (AsyncResult.isSuccess(result)) store.applyEvent(result.value);
      });
      queueMicrotask(() => {
        if (version === 0 && AsyncResult.isSuccess(initial)) store.applyEvent(initial.value);
      });
      return store;
    }).pipe(Atom.setIdleTTL(1_000), Atom.withLabel(`voice:conversation-sync:${sessionId}`));
    return { store, atom };
  }, [environmentId, sessionId]);

  // Mount the subscription for the component's lifetime.
  useAtomValue(session?.atom ?? inactiveSyncAtom);
  const store = session?.store ?? inactiveStore;
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);

  // Start a session when the overlay opens.
  useEffect(() => {
    if (!active || threadId === null) {
      setSessionId(null);
      return;
    }
    let cancelled = false;
    sequenceRef.current = 0;
    void (async () => {
      const result = await startConversation({ environmentId, input: { threadId } });
      if (cancelled) return;
      if (result._tag === "Success") {
        setSessionId(result.value.sessionId);
      } else if (!isAtomCommandInterrupted(result)) {
        toastManager.add({
          type: "error",
          title: "Voice conversation unavailable",
          description: "AvedaAgent could not start a live voice session for this Auftrag.",
        });
        onRequestClose();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, environmentId, threadId, startConversation, onRequestClose]);

  // Stop the session (and release the mic) when it ends or the component unmounts.
  useEffect(() => {
    if (!sessionId) return;
    const activeSessionId = sessionId;
    return () => {
      recorderRef.current?.cancel();
      recorderRef.current = null;
      setTurnState("idle");
      void stopConversation({ environmentId, input: { sessionId: activeSessionId } });
    };
  }, [environmentId, sessionId, stopConversation]);

  const sendRecordedTurn = useCallback(
    async (recorder: VoiceTurnRecorder, activeSessionId: string) => {
      setTurnState("sending");
      try {
        const captured = await recorder.stop();
        recorderRef.current = null;
        if (!captured) return;
        const sequence = sequenceRef.current;
        sequenceRef.current += 1;
        const appendResult = await appendAudio({
          environmentId,
          input: {
            sessionId: activeSessionId,
            sequence,
            mimeType: captured.mimeType,
            data: captured.data,
          },
        });
        if (appendResult._tag !== "Success") {
          if (!isAtomCommandInterrupted(appendResult)) {
            toastManager.add({
              type: "error",
              title: "Voice turn failed",
              description: "AvedaAgent could not send the recorded audio.",
            });
          }
          return;
        }
        const endResult = await endInput({ environmentId, input: { sessionId: activeSessionId } });
        if (endResult._tag !== "Success" && !isAtomCommandInterrupted(endResult)) {
          toastManager.add({
            type: "error",
            title: "Voice turn failed",
            description: "AvedaAgent could not complete the voice turn.",
          });
        }
      } finally {
        setTurnState("idle");
      }
    },
    [appendAudio, endInput, environmentId],
  );

  const toggleTurn = useCallback(() => {
    if (
      !sessionId ||
      isMuted ||
      turnState === "sending" ||
      (turnState !== "recording" && snapshot.phase !== "listening")
    ) {
      return;
    }
    const recorder = recorderRef.current;
    if (turnState === "recording" && recorder) {
      void sendRecordedTurn(recorder, sessionId);
      return;
    }
    const nextRecorder = new VoiceTurnRecorder();
    void (async () => {
      try {
        await nextRecorder.start();
        recorderRef.current = nextRecorder;
        store.beginUserTurn();
        setTurnState("recording");
      } catch (error) {
        nextRecorder.cancel();
        toastManager.add({
          type: "error",
          title: "Microphone unavailable",
          description:
            error instanceof Error ? error.message : "AvedaAgent could not access the microphone.",
        });
      }
    })();
  }, [isMuted, sendRecordedTurn, sessionId, snapshot.phase, store, turnState]);

  const toggleMute = useCallback(() => {
    setIsMuted((muted) => {
      const next = !muted;
      if (next && turnState === "recording") {
        recorderRef.current?.cancel();
        recorderRef.current = null;
        setTurnState("idle");
      }
      return next;
    });
  }, [turnState]);

  const end = useCallback(() => {
    onRequestClose();
  }, [onRequestClose]);

  const status: VoiceConversationStatus = !sessionId
    ? "connecting"
    : turnState === "recording"
      ? "recording"
      : turnState === "sending"
        ? "sending"
        : snapshot.phase;

  return {
    status,
    userTranscript: snapshot.userTranscript,
    assistantText: snapshot.assistantText,
    activeToolLabel: snapshot.activeToolLabel,
    errorMessage: snapshot.errorMessage,
    isRecording: turnState === "recording",
    isMuted,
    isBusy:
      turnState === "sending" || snapshot.phase === "thinking" || snapshot.phase === "speaking",
    canToggleTurn:
      sessionId !== null &&
      !isMuted &&
      turnState !== "sending" &&
      (turnState === "recording" || snapshot.phase === "listening"),
    toggleTurn,
    toggleMute,
    end,
  };
}
